import { AxiosError, AxiosRequestConfig } from 'axios';
import { useRef, useState } from 'react';
import { useAuth } from '../providers/auth';
import { api } from '../services/api';
import { useActionToast } from '../services/actionToast';

export interface ParamsType {
  search?: string | null;
  params?: object;
}

export interface ImageTypes {
  original: string;
  small: string;
  medium: string;
  large: string;
}

export interface PaginationType {
  has_next: boolean;
  has_prev: boolean;
  current_page: number;
  next_page?: number;
  items?: number;
  total_items: number;
  total_pages: number;
}

export interface DataType {
  pagination: PaginationType;
  status: string;
  msg: string;
  total: number | null;
  data: any;
}

export const EMPTY_FETCH_DATA: DataType = {
  pagination: {} as PaginationType,
  status: '',
  msg: '',
  total: null,
  data: [],
};

export const getFetchData = (value: DataType | null | undefined): DataType =>
  value || EMPTY_FETCH_DATA;

export interface ErrorType {
  status: string;
  msg: string;
}

export interface RequestFailure {
  ok: false;
  error: any;
}

export const isRequestFailure = (result: any): result is RequestFailure => result?.ok === false;

export interface UseFetchOptions {
  actionFeedback?: boolean;
  entityName?: string;
}

const entityLabels: Array<[string, string]> = [
  ['extra-material-templates', 'modelo de material'],
  ['extra_materials', 'material extra'],
  ['material-modules', 'módulo de material'],
  ['permissions', 'permissão'],
  ['notifications', 'notificação'],
  ['institutions', 'instituição'],
  ['secretaries', 'secretário'],
  ['rating/topics', 'tópico de avaliação'],
  ['ratings', 'avaliação'],
  ['categories', 'categoria'],
  ['contents', 'conteúdo'],
  ['classes', 'módulo'],
  ['weeks', 'semana'],
  ['children', 'aluno'],
  ['teams', 'turma'],
  ['users', 'usuário'],
  ['globals', 'configuração'],
  ['client', 'perfil'],
];

const getEntityName = (url: string, configuredName?: string) =>
  configuredName || entityLabels.find(([path]) => url.includes(path))?.[1] || 'item';

const getActionCopy = (method: string, url: string, entityName: string) => {
  if (url.includes('/images') || url.includes('/files')) {
    return {
      successTitle: 'Upload concluído',
      errorTitle: 'Upload não concluído',
      successDescription: `O upload de ${entityName} foi concluído com sucesso.`,
      errorDescription: `Não foi possível enviar ${entityName}.`,
    };
  }
  if (method === 'post') {
    return {
      successTitle: 'Criação concluída',
      errorTitle: 'Criação não concluída',
      successDescription: `A criação de ${entityName} foi concluída com sucesso.`,
      errorDescription: `Não foi possível criar ${entityName}.`,
    };
  }
  if (method === 'delete') {
    return {
      successTitle: 'Exclusão concluída',
      errorTitle: 'Exclusão não concluída',
      successDescription: `A exclusão de ${entityName} foi concluída com sucesso.`,
      errorDescription: `Não foi possível excluir ${entityName}.`,
    };
  }
  return {
    successTitle: 'Atualização concluída',
    errorTitle: 'Atualização não concluída',
    successDescription: `A atualização de ${entityName} foi concluída com sucesso.`,
    errorDescription: `Não foi possível atualizar ${entityName}.`,
  };
};

let mutationToastSequence = 0;

const getMutationToastId = (status: 'success' | 'error') => {
  mutationToastSequence += 1;
  return `mutation-${Date.now()}-${mutationToastSequence}-${status}`;
};

const serializeRequestValue = (value: any): string => {
  if (value instanceof FormData) {
    return JSON.stringify(
      Array.from(value.entries()).map(([key, entry]) => [
        key,
        entry instanceof File ? `${entry.name}:${entry.size}:${entry.type}` : entry,
      ])
    );
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value)) return `[${value.map(serializeRequestValue).join(',')}]`;
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${key}:${serializeRequestValue(value[key])}`)
      .join(',')}}`;
  }
  return String(value);
};

export const useFetch = (url: string, options: UseFetchOptions = {}) => {
  const { SilentLogout } = useAuth();
  const actionToast = useActionToast();
  const [data, setData] = useState<DataType | null>(null);
  const [error, setError] = useState<ErrorType | string | null | any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const activeMutations = useRef(0);
  const latestGetPending = useRef(false);
  const latestGetRequest = useRef(0);
  const mutationRequests = useRef<Map<string, Promise<any>>>(new Map());

  const requestApi = (
    method: 'get' | 'post' | 'put' | 'patch' | 'delete',
    config?: AxiosRequestConfig | ParamsType | any,
    updateUrl?: string | any | null,
    headers: any = null
  ) => {
    const isMutation = method !== 'get';
    const targetUrl = typeof updateUrl === 'string' && updateUrl ? updateUrl : url;
    const mutationKey = isMutation
      ? `${method}:${targetUrl}:${serializeRequestValue(config)}:${serializeRequestValue(headers)}`
      : '';
    const pendingMutation = mutationRequests.current.get(mutationKey);
    if (pendingMutation) return pendingMutation;

    const execute = async () => {
      const getRequestId = isMutation ? 0 : latestGetRequest.current + 1;
      if (isMutation) {
        activeMutations.current += 1;
      } else {
        latestGetRequest.current = getRequestId;
        latestGetPending.current = true;
      }
      setLoading(true);
      setError(null);
      try {
        let response;
        let axiosConfig = config;
        if ((method === 'get' || method === 'delete') && config) {
          const isAlreadyAxiosConfig =
            'params' in config ||
            'headers' in config ||
            'baseURL' in config ||
            'timeout' in config ||
            'responseType' in config;

          if (!isAlreadyAxiosConfig) axiosConfig = { params: config };
        }

        if (headers) {
          response = await api[method](targetUrl, axiosConfig, headers);
        } else {
          response = await api[method](targetUrl, axiosConfig);
        }

        if (isMutation || getRequestId === latestGetRequest.current) setData(response.data);
        if (isMutation && options.actionFeedback !== false) {
          const entityName = getEntityName(targetUrl, options.entityName);
          const copy = getActionCopy(method, targetUrl, entityName);
          actionToast({
            status: 'success',
            title: copy.successTitle,
            description: copy.successDescription,
            id: getMutationToastId('success'),
          });
        }
        return response;
      } catch (requestError) {
        const err = requestError as AxiosError | ErrorType | any;
        if (err.response?.status === 422) SilentLogout();
        if (isMutation || getRequestId === latestGetRequest.current) {
          setError(err.response?.data || err);
        }
        if (isMutation && options.actionFeedback !== false) {
          const entityName = getEntityName(targetUrl, options.entityName);
          const copy = getActionCopy(method, targetUrl, entityName);
          actionToast({
            status: 'error',
            title: copy.errorTitle,
            description: copy.errorDescription,
            id: getMutationToastId('error'),
          });
        }
        return { ok: false, error: err } as RequestFailure;
      } finally {
        if (isMutation) {
          activeMutations.current -= 1;
        } else if (getRequestId === latestGetRequest.current) {
          latestGetPending.current = false;
        }
        setLoading(activeMutations.current > 0 || latestGetPending.current);
      }
    };

    const request = execute();
    if (isMutation) {
      mutationRequests.current.set(mutationKey, request);
      request.finally(() => {
        mutationRequests.current.delete(mutationKey);
      });
    }
    return request;
  };

  return { requestApi, data, error, loading };
};
